import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldAlert, Camera, RotateCcw, Trash2, History, Clock, Database, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * Demo Data Reset — admin-only safety-netted reset of run-time data so the
 * admin can rehearse the week multiple times with a fresh state without
 * losing seeded content (mentors, challenges, businesses, resources, users).
 *
 * Failsafes:
 *   1. Auto-snapshot before every Reset
 *   2. Reset requires the admin to type "RESET" exactly into a field
 *   3. Second confirmation via AlertDialog ("Are you absolutely sure?")
 *   4. Restore the latest (or any) snapshot at any time
 */

const RESET_KEYWORD = "RESET";

function StatBlock({ label, value }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-3" data-testid={`reset-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-2xl font-black tracking-tighter mt-1">{value}</div>
    </div>
  );
}

export default function AdminDemoReset() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;

  if (!isSuperAdmin) {
    return (
      <div className="space-y-6" data-testid="admin-demo-reset-restricted">
        <div className="border-2 border-[#0A0A0A] bg-[#F3F4F6] p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
              <Lock size={22} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow">Restricted · platform owner only</div>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">
                Data reset is locked
              </h1>
              <p className="mt-3 text-sm text-[#0A0A0A] leading-relaxed max-w-3xl">
                The destructive Data Reset and Snapshot Restore actions are firewalled to the
                platform owner. If you need cohort data cleared or restored,
                please ask James (james@wearetheweave.co.uk) to run it — he can
                take a snapshot first so nothing is lost.
              </p>
              <p className="mt-3 text-xs text-[#525252] leading-relaxed max-w-3xl">
                All other admin functions (roster, sponsors, mentors, sessions,
                resources, attendance, mentor slots, etc.) work normally for you.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <AdminDemoResetInner />;
}

function AdminDemoResetInner() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demo-reset/snapshots");
      setSnapshots(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load snapshots");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const takeSnapshot = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-reset/snapshot", { label: "manual snapshot" });
      toast.success(`Snapshot saved — ${data.snapshot.id.slice(0, 8)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save snapshot");
    } finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-reset", { confirm: RESET_KEYWORD });
      toast.success("Demo data reset. Auto-snapshot saved — use Restore to undo.");
      setConfirmText("");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const doRestore = async (snapshotId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-reset/restore", { snapshot_id: snapshotId });
      toast.success(`Restored from snapshot ${data.restored_from.id.slice(0, 8)}`);
      setPendingRestore(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm("Delete this snapshot? Cannot be undone.")) return;
    try {
      await api.delete(`/admin/demo-reset/snapshots/${id}`);
      toast.success("Snapshot deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const canReset = confirmText.trim().toUpperCase() === RESET_KEYWORD;

  return (
    <div className="space-y-10" data-testid="admin-demo-reset-page">
      <div className="border-2 border-[#A23232] bg-[#FDEDED] p-6 md:p-8 relative" data-testid="reset-header-callout">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#A23232] text-white flex items-center justify-center shrink-0">
            <ShieldAlert size={22} />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-[#A23232]">Admin · destructive action</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">
              Demo data reset
            </h1>
            <p className="mt-3 text-sm text-[#0A0A0A] leading-relaxed max-w-3xl">
              Clears every team&apos;s run-time data so you can rehearse the week with a clean slate.
              Snapshots are taken automatically before every reset — if you reset by accident, hit{" "}
              <strong>Restore latest</strong> below.
            </p>
            <p className="mt-2 text-xs text-[#7A1F1F] leading-relaxed max-w-3xl">
              <strong>What gets cleared:</strong> founding-business claims, target shortlists + finalisations,
              CEO reports, challenge submissions + scores, broadcasts, journals, retrospective survey responses,
              uploaded files, and mentor-slot bookings (slots themselves are kept).<br />
              <strong>What is preserved:</strong> users + teams (team join codes intact), challenges, businesses
              & target businesses (their seeded metadata), mentors + speakers + resources + days/sessions.
            </p>
          </div>
        </div>
      </div>

      {/* Snapshot section */}
      <section className="border border-[#E5E7EB] bg-white p-6" data-testid="snapshot-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Camera size={12} /> Step 1 · Snapshot</div>
            <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Take a manual snapshot</h2>
            <p className="text-xs text-[#525252] mt-2 max-w-md leading-relaxed">
              Optional — a snapshot is always taken automatically before a Reset. Keep at most 10; oldest auto-pruned.
            </p>
          </div>
          <Button
            onClick={takeSnapshot}
            disabled={busy}
            className="rounded-none bg-[#0A0A0A] hover:bg-[#1a1a1a]"
            data-testid="snapshot-create-btn"
          >
            <Camera size={13} className="mr-2" /> {busy ? "Saving…" : "Take snapshot now"}
          </Button>
        </div>
      </section>

      {/* Selective clear */}
      <SelectiveClearSection />

      {/* Reset section */}
      <section className="border-2 border-[#A23232] bg-white p-6" data-testid="reset-section">
        <div className="label-eyebrow flex items-center gap-2 text-[#A23232]"><Trash2 size={12} /> Step 2 · Reset</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Wipe run-time data</h2>
        <p className="text-xs text-[#525252] mt-2 max-w-md leading-relaxed">
          Type <code className="bg-[#0A0A0A] text-white px-2 py-0.5 font-mono">{RESET_KEYWORD}</code> below to enable the reset button.
          You&apos;ll be asked one more time to confirm.
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end max-w-xl">
          <div>
            <Label className="label-eyebrow">Confirmation phrase</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESET to enable"
              className="rounded-none mt-2 font-mono tracking-widest uppercase"
              data-testid="reset-confirm-input"
            />
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canReset || busy}
            className={`rounded-none ${canReset ? "bg-[#A23232] hover:bg-[#8a2828]" : "bg-[#E5E7EB] text-[#525252]"}`}
            data-testid="reset-trigger-btn"
          >
            <Trash2 size={13} className="mr-2" /> Reset demo data
          </Button>
        </div>
      </section>

      {/* Snapshots list */}
      <section data-testid="snapshots-section">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><History size={12} /> Step 3 · Restore</div>
            <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Snapshots</h2>
            <p className="text-xs text-[#525252] mt-2 max-w-md leading-relaxed">
              Newest first. Hit <strong>Restore</strong> to revert run-time data to that point — useful if you
              reset by accident, or want to wind back to before a specific moment.
            </p>
          </div>
          {snapshots[0] && (
            <Button
              onClick={() => setPendingRestore(snapshots[0])}
              disabled={busy}
              className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]"
              data-testid="restore-latest-btn"
            >
              <RotateCcw size={13} className="mr-2" /> Restore latest
            </Button>
          )}
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-[#525252]">Loading snapshots…</div>
        ) : snapshots.length === 0 ? (
          <div className="mt-6 border border-dashed border-[#E5E7EB] p-6 text-center text-sm text-[#525252]" data-testid="snapshots-empty">
            No snapshots yet. Take one before any major change.
          </div>
        ) : (
          <ul className="mt-6 space-y-3">
            {snapshots.map((s) => (
              <li
                key={s.id}
                className="border border-[#E5E7EB] bg-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                data-testid={`snapshot-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg font-bold tracking-tight">{s.label}</div>
                    {s.auto && (
                      <span className="text-[9px] uppercase tracking-wider bg-[#f4b847] text-[#0A0A0A] px-2 py-0.5 font-bold">Auto</span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-1 flex items-center gap-1">
                    <Clock size={10} /> {new Date(s.created_at).toLocaleString()}
                    <span className="ml-2 font-mono text-[#0A0A0A] bg-[#F3F4F6] px-1.5 py-0.5">{s.id.slice(0, 8)}</span>
                  </div>
                  {s.stats && (
                    <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2 text-[10px]">
                      <StatBlock label="Subs" value={s.stats.submissions ?? 0} />
                      <StatBlock label="Reports" value={s.stats.ceo_reports ?? 0} />
                      <StatBlock label="Journals" value={s.stats.journals ?? 0} />
                      <StatBlock label="Targets" value={s.stats.target_claims ?? 0} />
                      <StatBlock label="Bookings" value={s.stats.booked_slots ?? 0} />
                      <StatBlock label="Files" value={s.stats.files ?? 0} />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    onClick={() => setPendingRestore(s)}
                    disabled={busy}
                    variant="outline"
                    className="rounded-none border-[#2596be] text-[#1e7da3] hover:bg-[#2596be] hover:text-white"
                    data-testid={`restore-btn-${s.id}`}
                  >
                    <RotateCcw size={13} className="mr-2" /> Restore
                  </Button>
                  <Button
                    onClick={() => deleteSnapshot(s.id)}
                    disabled={busy}
                    variant="outline"
                    className="rounded-none border-[#A23232] text-[#A23232] hover:bg-[#A23232] hover:text-white"
                    data-testid={`delete-btn-${s.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* RESET — final confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-none border-2 border-[#A23232]" data-testid="reset-final-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter text-[#A23232]">
              Are you absolutely sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              This will wipe every team&apos;s run-time data RIGHT NOW. A snapshot will be taken automatically
              before the wipe, so you can restore it — but anything created after this moment will be lost
              until you restore. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="reset-final-cancel-btn">No, take me back</AlertDialogCancel>
            <AlertDialogAction
              onClick={doReset}
              disabled={busy}
              className="rounded-none bg-[#A23232] hover:bg-[#8a2828] text-white"
              data-testid="reset-final-confirm-btn"
            >
              <Trash2 size={13} className="mr-2" /> {busy ? "Resetting…" : "Yes, reset everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* RESTORE — confirm dialog */}
      <AlertDialog open={!!pendingRestore} onOpenChange={(o) => !o && setPendingRestore(null)}>
        <AlertDialogContent className="rounded-none border-2 border-[#2596be]" data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter">
              Restore this snapshot?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              Run-time data will revert to <strong>{pendingRestore?.label}</strong> ({pendingRestore?.created_at ? new Date(pendingRestore.created_at).toLocaleString() : ""}).
              Anything submitted since that snapshot will be replaced.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="restore-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingRestore && doRestore(pendingRestore.id)}
              disabled={busy}
              className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] text-white"
              data-testid="restore-confirm-btn"
            >
              <RotateCcw size={13} className="mr-2" /> {busy ? "Restoring…" : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function SelectiveClearSection() {
  const [collections, setCollections] = useState([]);
  const [chosen, setChosen] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/demo-reset/collections");
      setCollections(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const canClear = chosen && confirmText.trim().toUpperCase() === "RESET";
  const current = collections.find((c) => c.collection === chosen);

  const clear = async () => {
    if (!canClear) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-reset/clear", {
        collection: chosen, confirm: "RESET",
      });
      toast.success(`Cleared ${data.deleted} from ${current?.label || chosen}`);
      setChosen(""); setConfirmText("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clear");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-[#E5E7EB] bg-white p-6" data-testid="selective-clear-section">
      <div className="label-eyebrow flex items-center gap-2"><Database size={12} /> Step 1b · Selective clear</div>
      <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Clear one thing at a time</h2>
      <p className="text-xs text-[#525252] mt-2 max-w-md leading-relaxed">
        Wipe a single collection while leaving the rest intact — useful when you
        just want to clear e.g. attendance without resetting everything. An
        auto-snapshot is still taken first, so you can undo.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end max-w-3xl">
        <div>
          <Label className="label-eyebrow">Collection</Label>
          <select
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
            data-testid="selective-clear-select"
            className="mt-2 w-full h-11 border border-[#E5E7EB] bg-white px-3 text-sm"
          >
            <option value="">— Choose what to clear —</option>
            {collections.map((c) => (
              <option key={c.collection} value={c.collection}>
                {c.label} ({c.count})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="label-eyebrow">Type RESET to enable</Label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESET"
            className="rounded-none mt-2 font-mono tracking-widest uppercase"
            data-testid="selective-clear-confirm"
          />
        </div>
        <Button
          onClick={clear}
          disabled={!canClear || busy}
          className={`rounded-none ${canClear ? "bg-[#A23232] hover:bg-[#8a2828]" : "bg-[#E5E7EB] text-[#525252]"}`}
          data-testid="selective-clear-btn"
        >
          <Trash2 size={13} className="mr-2" /> {busy ? "Clearing…" : "Clear this"}
        </Button>
      </div>
    </section>
  );
}
